import { ArrowRight, Boxes } from "lucide-react";
import { Link } from "react-router-dom";

import { useFlows } from "@/api/flows";
import { EmptyState } from "@/components/layout/EmptyState";
import { cardVariants } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/**
 * "Start a run" launchpad for the project overview (#525).
 *
 * Flow templates used to be reachable only via Submit run → Submit a
 * Flow, so a first-time user on the overview saw "No runs yet" and no
 * sign that AFFDO existed at all. This panel lists what /api/flows
 * serves and deep-links each template into the submission page with
 * ``?flow=<name>`` preselected — the same query param SubmitFlowPage
 * already reads, so no new contract.
 *
 * Deliberately quiet when the catalog is empty or unreachable: the
 * overview must still render its runs feed, so a flows outage degrades
 * to a hint rather than an error page.
 */
export interface FlowLaunchPanelProps {
  /** Project slug — the submission route is project-scoped. */
  slug: string;
}

export function flowSubmitPath(slug: string, flowName?: string): string {
  const base = `/projects/${slug}/submit/flow`;
  return flowName ? `${base}?flow=${encodeURIComponent(flowName)}` : base;
}

export function FlowLaunchPanel({ slug }: FlowLaunchPanelProps): JSX.Element {
  const flows = useFlows();

  return (
    <section aria-labelledby="flows-heading" className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h2 id="flows-heading" className="font-display text-xl font-semibold tracking-tight">
          Start a run
        </h2>
        <Link
          to={flowSubmitPath(slug)}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          All templates
        </Link>
      </div>

      {flows.isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      ) : flows.isError ? (
        <EmptyState
          title="Could not load Flow templates."
          description={
            flows.error instanceof Error
              ? flows.error.message
              : "The FlowStack server returned an error."
          }
        />
      ) : !flows.data || flows.data.length === 0 ? (
        <EmptyState
          title="No Flow templates available."
          description="No Flows were found in the server's configured flows directory."
        />
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {flows.data.map((flow) => (
            <li key={flow.name} className="flex">
              <Link
                to={flowSubmitPath(slug, flow.name)}
                // Card-shaped, but a link: the recipe arrives as a class
                // string so the element stays an `<a>` with its own hover and
                // focus treatment — see `cardVariants`' own comment.
                className={cn(
                  cardVariants(),
                  "group flex flex-1 flex-col gap-2 p-card transition-colors hover:border-border-strong hover:bg-surface-2"
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Boxes className="h-4 w-4 text-muted-foreground" aria-hidden />
                    <h3 className="font-display text-sm font-semibold tracking-tight text-foreground">
                      {flow.display_name || flow.name}
                    </h3>
                  </div>
                  <span className="rounded-sm bg-surface-2 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                    v{flow.version}
                  </span>
                </div>
                {flow.description && (
                  <p className="line-clamp-2 text-xs text-muted-foreground">{flow.description}</p>
                )}
                <div className="mt-auto flex items-center justify-between gap-2 pt-1">
                  <span className="inline-flex items-center gap-1 rounded-full border border-border-subtle bg-surface-2 px-1.5 py-0.5 text-[10px] text-muted-foreground">
                    {flow.node_count} {flow.node_count === 1 ? "node" : "nodes"}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground group-hover:text-foreground">
                    Configure
                    <ArrowRight className="h-3 w-3" aria-hidden />
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
